import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container } from 'react-bootstrap';
import { ShieldCheck } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import InlineCheckoutForm from '../../components/InlineCheckoutForm/InlineCheckoutForm';
import LoginRedirect from '../../components/LoginRedirect';
import { buildSalesLeadCheckoutParams } from '../../utils/salesLeadCheckoutParams';
import useSalesPromptRouting from '../../hooks/useSalesPromptRouting';
import useABTest from '../../hooks/useABTest';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import {
  GOOGLE_SEARCH_JOBS,
  GOOGLE_SEARCH_LP_PATH,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_EXPERIMENT_NAME,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_FEATURE_FLAG,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_PREVIEW_PARAM,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS,
  getGoogleSearchJob,
  getGoogleSearchSignupExperienceVariantConfig,
  normalizeGoogleSearchSignupExperienceVariant,
  resolveGoogleSearchJobId,
} from './googleSearchLandingCopy';

import './SalesPage.css';
import './SalesPage07.css';
import './AuthFirstSalesPage.css';
import './GoogleSearchLandingPage.css';

const GOOGLE_SEARCH_SIGNUP_EXPERIENCE = (
  ACTIVE_EXPERIMENTS.GOOGLE_SEARCH_SIGNUP_EXPERIENCE_V2
);

const buildExperimentReturnTo = ({ baseReturnTo, experimentContext, jobId, searchParams }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://botspot.trade';
  const parsed = new URL(baseReturnTo || '/sales-choose', origin);
  const params = parsed.searchParams;

  Object.entries(experimentContext).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  params.set('google_search_lp_job', jobId);

  [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'postSignupCheckoutVariant',
  ].forEach((key) => {
    const value = searchParams.get(key);
    if (value && !params.has(key)) {
      params.set(key, value);
    }
  });

  const query = params.toString();
  return `${parsed.pathname}${query ? `?${query}` : ''}${parsed.hash}`;
};

export default function GoogleSearchLandingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    showLoginHint,
    loginReturnTo,
    newSignupReturnTo,
    getLeadSuccessPath,
    seedPromptIntent,
  } = useSalesPromptRouting({ pageVariant: 'google_search_lp' });

  const utmData = useMemo(() => getUtmParamsForApi(), []);
  const jobId = resolveGoogleSearchJobId({
    job: searchParams.get('job'),
    utmCampaign: searchParams.get('utm_campaign') || utmData?.utm_campaign,
    prompt: searchParams.get('prompt'),
  });
  const job = getGoogleSearchJob(jobId);
  const rawPreviewVariant = searchParams.get(GOOGLE_SEARCH_SIGNUP_EXPERIENCE_PREVIEW_PARAM);
  const previewVariant = (
    rawPreviewVariant && GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS[rawPreviewVariant]
      ? rawPreviewVariant
      : null
  );
  const experimentName = (
    GOOGLE_SEARCH_SIGNUP_EXPERIENCE?.name
    || GOOGLE_SEARCH_SIGNUP_EXPERIENCE_EXPERIMENT_NAME
  );
  const featureFlag = (
    GOOGLE_SEARCH_SIGNUP_EXPERIENCE?.featureFlag
    || GOOGLE_SEARCH_SIGNUP_EXPERIENCE_FEATURE_FLAG
  );
  const experimentTrackingData = useMemo(() => ({
    forcedVariant: previewVariant,
    google_search_lp_job: jobId,
    normalizeControlExposure: true,
    skipFeatureFlagEvaluation: Boolean(previewVariant),
    source: location.pathname,
    trackForcedExposure: false,
    validVariants: Object.keys(GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS),
    ...utmData,
  }), [jobId, location.pathname, previewVariant, utmData]);
  const {
    variant: assignedVariant,
    isLoading: isExperimentLoading,
    isExperimentActive,
  } = useABTest(
    experimentName,
    featureFlag,
    'control',
    experimentTrackingData,
  );
  const variantId = normalizeGoogleSearchSignupExperienceVariant(
    previewVariant || assignedVariant
  );
  const variantConfig = getGoogleSearchSignupExperienceVariantConfig(variantId);
  const hasResolvedExperiment = Boolean(previewVariant) || !isExperimentLoading;
  const salesPageDesignVariant = variantId === 'auth_first'
    ? 'google_search_auth_first'
    : 'google_search_email_capture';
  const experimentContext = useMemo(() => ({
    experiment_name: experimentName,
    experiment_variant: variantId,
    feature_flag: featureFlag,
    [`$feature/${featureFlag}`]: variantId,
    [`$feature_flag_${featureFlag}`]: variantId,
    google_search_lp_job: jobId,
    google_search_signup_experience_v2_experiment_name: experimentName,
    google_search_signup_experience_v2_variant: variantId,
    sales_page_design_variant: salesPageDesignVariant,
  }), [experimentName, featureFlag, jobId, salesPageDesignVariant, variantId]);
  const authReturnTo = useMemo(() => buildExperimentReturnTo({
    baseReturnTo: loginReturnTo,
    experimentContext,
    jobId,
    searchParams,
  }), [experimentContext, jobId, loginReturnTo, searchParams]);
  const authNewSignupReturnTo = useMemo(() => buildExperimentReturnTo({
    baseReturnTo: newSignupReturnTo,
    experimentContext,
    jobId,
    searchParams,
  }), [experimentContext, jobId, newSignupReturnTo, searchParams]);

  useEffect(() => {
    const existingPrompt = (searchParams.get('prompt') || '').trim();
    const existingJob = (searchParams.get('job') || '').trim().toLowerCase();
    if (existingPrompt && existingJob === jobId) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    if (!existingJob || !GOOGLE_SEARCH_JOBS[existingJob] || existingJob !== jobId) {
      params.set('job', jobId);
    }
    if (!existingPrompt && job.defaultPrompt) {
      params.set('prompt', job.defaultPrompt);
    }

    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      navigate(`${GOOGLE_SEARCH_LP_PATH}?${nextQuery}`, { replace: true });
    }
  }, [job.defaultPrompt, jobId, navigate, searchParams]);

  useEffect(() => {
    if (typeof seedPromptIntent === 'function') {
      seedPromptIntent();
    }
  }, [seedPromptIntent, searchParams]);

  useEffect(() => {
    window.__experimentContext = experimentContext;
  }, [experimentContext]);

  useEffect(() => {
    if (!hasResolvedExperiment) {
      return;
    }

    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'google_search_lp',
      source: location.pathname,
      variant: variantId,
      job: jobId,
      is_experiment_active: previewVariant ? false : isExperimentActive,
      experiment_preview: Boolean(previewVariant),
      ...experimentContext,
      ...utmData,
    });
  }, [
    experimentContext,
    hasResolvedExperiment,
    isExperimentActive,
    jobId,
    location.pathname,
    previewVariant,
    utmData,
    variantId,
  ]);

  const handleLoginClick = () => {
    const safeLoginReturnTo = loginReturnTo || '/';
    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'google_search_lp_link',
      source: location.pathname,
      returnTo: safeLoginReturnTo,
      google_search_lp_job: jobId,
      experiment_variant: variantId,
    });
    navigate(`/login?returnTo=${encodeURIComponent(safeLoginReturnTo)}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  const handleContactSuccess = ({
    contactData,
    leadTransactionId,
    autoLoginToken,
    howFoundUs,
    howFoundUsOtherText,
    existingUser,
    redirectTo,
  }) => {
    if (existingUser && redirectTo) {
      navigate(redirectTo);
      return;
    }

    const params = buildSalesLeadCheckoutParams({
      contactData,
      leadTransactionId,
      variant: variantId,
      howFoundUs,
      howFoundUsOtherText,
      extra: {
        experiment_name: experimentName,
        experiment_variant: variantId,
        feature_flag: featureFlag,
        ...experimentContext,
      },
    });

    navigate(getLeadSuccessPath(params, autoLoginToken));
  };

  const accent = job.headlineAccent;
  const headlineMain = job.headline.endsWith(` ${accent}`)
    ? job.headline.slice(0, -(accent.length + 1)).trim()
    : job.headline;

  if (variantId === 'auth_first') {
    return (
      <>
        <Helmet>
          <title>{`${job.headline} | BotSpot`}</title>
          <meta name="description" content={job.subhead} />
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={`https://botspot.trade${GOOGLE_SEARCH_LP_PATH}`} />
          <meta name="theme-color" content="#080b18" />
        </Helmet>

        <div
          className="google-search-auth-first-page auth-first-sales auth-page bsui-scope"
          data-bs-theme="dark"
          data-design="google-search-job"
          data-experiment-loading={String(isExperimentLoading)}
          data-job={jobId}
          data-signup-experience={variantId}
          data-testid="google-search-lp"
        >
          <main className="google-search-auth-first__frame">
            <header className="google-search-auth-first__masthead">
              <a aria-label="BotSpot home" href="/">
                <img alt="BotSpot by LumiWealth" height={429} src={botspotLogo} width={1385} />
              </a>
              <span>botspot.trade</span>
            </header>

            <div className="google-search-auth-first__grid">
              <section
                aria-labelledby="google-search-auth-first-title"
                className="google-search-auth-first__pitch"
                data-testid="google-search-auth-first-pitch"
              >
                <div className="google-search-auth-first__badge">
                  <span aria-hidden="true" />
                  {job.badge}
                </div>

                <h1
                  className="google-search-auth-first__title"
                  data-testid="google-search-lp-headline"
                  id="google-search-auth-first-title"
                >
                  {headlineMain}{' '}
                  <span>{accent}</span>
                </h1>

                <p
                  className="google-search-auth-first__subhead"
                  data-testid="google-search-lp-subhead"
                >
                  {job.subhead}
                </p>

                <ul
                  className="google-search-auth-first__proof"
                  data-testid="google-search-lp-proof"
                >
                  {job.proof.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>

                <div className="google-search-auth-first__workflow">
                  <div className="google-search-auth-first__workflow-copy">
                    <strong>{job.authFirst.evidenceLabel}</strong>
                    <span>{job.authFirst.evidenceDescription}</span>
                  </div>
                  <span className="google-search-auth-first__workflow-meta">
                    {job.authFirst.evidenceMeta}
                  </span>
                  <div className="google-search-auth-first__workflow-steps">
                    {job.authFirst.steps.map((step) => <span key={step}>{step}</span>)}
                  </div>
                </div>

                <div className="google-search-auth-first__independence">
                  <ShieldCheck aria-hidden="true" focusable="false" />
                  <span>Independent from Schwab and thinkorswim. You approve every action.</span>
                </div>
              </section>

              <aside
                aria-label="Create your BotSpot account"
                className="google-search-auth-first__auth auth-first-sales__conversion"
              >
                <div className="auth-first-sales__auth-card">
                  <LoginRedirect
                    analyticsContext={experimentContext}
                    descriptionOverride="Continue with Google or email. Your selected workflow stays ready."
                    headingOverride="Create your BotSpot account"
                    newSignupReturnToOverride={authNewSignupReturnTo}
                    productLedOverride
                    returnToOverride={authReturnTo}
                    sourceOverride="google_search_auth_first"
                  />
                </div>

                <div className="auth-first-sales__assurance">
                  <ShieldCheck aria-hidden="true" focusable="false" />
                  <p>
                    Signing in does not connect a broker or place a trade. {variantConfig.footer}
                  </p>
                </div>
              </aside>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${job.headline} | BotSpot`}</title>
        <meta name="description" content={job.subhead} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`https://botspot.trade${GOOGLE_SEARCH_LP_PATH}`} />
      </Helmet>

      <div
        className="sales-page-minimal v07-assistant-theme google-search-lp"
        data-experiment-loading={String(isExperimentLoading)}
        data-job={jobId}
        data-signup-experience={variantId}
        data-testid="google-search-lp"
      >
        <div className="hero-background">
          <div className="v07-pulse-glow" />
          <div className="v07-data-stream" />
          <div className="floating-shapes">
            <div className="shape v07-shape-primary" />
            <div className="shape v07-shape-accent" />
          </div>
        </div>

        <Container>
          <div className="content-wrapper v07-centered-layout">
            <div className="hero-content">
              <div className="v07-badge-wrapper">
                <span className="v07-status-dot" />
                <span className="v07-badge-text">{job.badge}</span>
              </div>

              <h1 className="hero-title v07-headline" data-testid="google-search-lp-headline">
                {headlineMain}{' '}
                <span className="v07-gradient-text">{accent}</span>
              </h1>

              <p className="hero-subtitle v07-sub-headline" data-testid="google-search-lp-subhead">
                {job.subhead}
              </p>

              <ul className="google-search-lp__proof" data-testid="google-search-lp-proof">
                {job.proof.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              <div className="v07-form-glass-card">
                <InlineCheckoutForm
                  onContactSuccess={handleContactSuccess}
                  contactButtonLabel={variantConfig.contactButtonLabel}
                  showNameField={false}
                  showPhoneField={false}
                  emailPlaceholder="Email address"
                />
                <p className="v07-trust-footer">Independent from Schwab and thinkorswim</p>
                <p className="v07-no-cc-text">{variantConfig.footer}</p>
              </div>

              {showLoginHint && (
                <div className="v07-login-hint">
                  Already a member?{' '}
                  <button type="button" onClick={handleLoginClick} className="v07-login-btn">
                    Log in
                  </button>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}
